import {
  Box,
  Divider,
  Flex,
  Skeleton,
  Spinner,
  Td,
  Text,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import React, { useState } from 'react';

import {
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement, Reporting } from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import { useGetUserQuery } from '../../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { ReportingGenerateWorkleaseModal } from './ReportingGeneratingWorkleaseModal';
import {
  createReportingInputShell,
  ReportingInputConfig,
} from './ReportingInputConfig';
import {
  createReportingDocxOutputConfigShell,
  createReportingXlsxOutputConfigShell,
  ReportingOutputConfig,
} from './ReportingOutputConfig';
import { finleyStandardDocxReportConfigTypes } from './ReportingOutputDocxConfig';
import {
  DeleteIconButton,
  ResetIconButton,
  SaveIconButton,
  UploadIconButton,
} from 'packages/common-frontend/components/IconButton';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import {
  formatSelectOptions,
  Select,
} from 'packages/common-frontend/components/Select';
import {
  errorToastAdminConfig,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';
import {
  useGenerateReportingOutputFileDevOnlyMutation,
  useProduceReportingGenerateMessageMutation,
} from 'packages/reporting/requests/reporting.gql.generated';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';

export const createReportingXlsxConfigShell = (): CreditAgreement.Config.Reporting.XlsxConfig => {
  return {
    type: 'xlsx',
    input: createReportingInputShell(),
    output: createReportingXlsxOutputConfigShell(),
  };
};

type ReportingPropertyConfigProps = {
  organizationId: string;
  creditAgreementId: string;
  reportingProperty: CreditAgreement.DomainEntity.ReportingProperty;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  configPropertyKey: string;
  columnLength: number;
  bankAccounts: CreditAgreement.DomainEntity.Items.BankAccountEntity[];
};
export const ReportingPropertyConfig = ({
  organizationId,
  creditAgreementId,
  reportingProperty,
  wipConfig,
  configPropertyKey,
  columnLength,
  bankAccounts,
}: ReportingPropertyConfigProps) => {
  const { name: reportName, _config_, reportConfigTypes } = reportingProperty;

  const toast = useToast();
  const [queryResults, setQueryResults] = useState<
    CreditAgreement.Config.QueryResult[]
  >([]);
  const [
    showProduceGenerateModal,
    setShowProduceGenerateModal,
  ] = useState<boolean>(false);
  const [
    showDevGenerateReportModal,
    setShowDevGenerateReportModal,
  ] = useState<boolean>(false);

  const [
    produceGenerateMessage,
    { loading: isProduceGenerateMessageLoading },
  ] = useProduceReportingGenerateMessageMutation({
    onError: (error) => {
      const errorMessage = getErrorMessageFromApolloError({ error });
      toast({
        ...errorToastAdminConfig,
        title: 'An error occurred while attempting to produce report.',
        description: errorMessage,
      });
    },
    onCompleted: ({ result }) => {
      switch (result.status) {
        case 'success':
          toast({
            title: 'Produce Report',
            description: 'Successfully produced message to generate report',
            status: 'info',
            position: 'top',
            duration: 5000,
            isClosable: true,
          });
          break;
        case 'error':
          toast({
            title: 'Produce Report',
            description:
              'Failed to produce message to generate report. Please contact Finley support',
            status: 'error',
            position: 'top',
            duration: 8000,
            isClosable: true,
          });
          break;
        default:
          finleyAssertUnreachable(result.status);
      }
    },
  });

  const [
    generateDevReport,
    { loading: isGenerateDevReportLoading },
  ] = useGenerateReportingOutputFileDevOnlyMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: ({ result }) => {
      switch (result.status) {
        case 'success':
          toast({
            title: '[DEV] Generate Report',
            description: 'Successfully generated report',
            status: 'info',
            position: 'top',
            duration: 5000,
            isClosable: true,
          });
          break;
        case 'error':
          toast({
            title: '[DEV] Generate Report',
            description: 'Failed to generate report. Please check logs',
            status: 'error',
            position: 'top',
            duration: 8000,
            isClosable: true,
          });
          break;
        default:
          finleyAssertUnreachable(result.status);
      }
    },
  });

  const { data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  if (!userData || !userData.user) {
    return <Skeleton />;
  }
  const userId = userData.user.id;
  const initiatingOrganizationId = userData.user.organizationId;

  const reportTypeConfigsWithFinleyStandardTemplate: CreditAgreement.DomainEntity.ReportConfigType[] = reportConfigTypes.filter(
    (reportConfigType) =>
      finleyStandardDocxReportConfigTypes.includes(reportConfigType),
  );

  return (
    <Tr>
      <Td colSpan={columnLength}>
        <Box mt="2">
          <Flex alignItems="center">
            <Text as="h2" fontSize="2xl" fontWeight="bold" mr="2">
              Config
            </Text>
            {wipConfig?.isMutateAllowed(configPropertyKey) && (
              <>
                <SaveIconButton
                  ariaLabel="save config"
                  label="Save Config"
                  onClick={() => {
                    const { values } = wipConfig.formProps;
                    const reportingConfig = getIn(values, configPropertyKey);
                    const configErrors = reportingValidate.validateReportingConfig(
                      { config: reportingConfig },
                    );
                    if (configErrors.length > 0) {
                      toast({
                        title: 'Reporting Config Errors',
                        description: configErrors.join(', '),
                        status: 'error',
                        position: 'top',
                        duration: 10000,
                        isClosable: true,
                      });
                      return;
                    }

                    wipConfig.saveCreditAgreementFullArtifactWip({
                      variables: {
                        creditAgreementFullArtifactWip: {
                          id: values.id,
                          creditFacilityId: values.creditFacilityId,
                          creditAgreement: JSON.stringify(values),
                        },
                      },
                    });
                  }}
                />

                {isProduceGenerateMessageLoading ? (
                  <Spinner size="sm" mx="2" p="2" />
                ) : (
                  <UploadIconButton
                    ariaLabel="produce report"
                    label="Produce Report"
                    onClick={() => {
                      setShowProduceGenerateModal(true);
                    }}
                  />
                )}

                {/*
                 * Only exposing this QA tool on Dev since don't want to lock
                 * up FinleyCore when generating reports
                 */}
                {commonUtils.EnvUtils.isDevelopment() &&
                  (isGenerateDevReportLoading ? (
                    <Spinner size="sm" mx="2" p="2" />
                  ) : (
                    <ResetIconButton
                      ariaLabel="qa report"
                      label="QA Report (Dev)"
                      onClick={() => {
                        setShowDevGenerateReportModal(true);
                      }}
                    />
                  ))}

                <DeleteIconButton
                  ariaLabel="reset config"
                  label="Reset Configuration"
                  onClick={() => {
                    wipConfig.formProps.setFieldValue(
                      configPropertyKey,
                      createReportingXlsxConfigShell(),
                    );
                  }}
                />
              </>
            )}
          </Flex>

          {wipConfig?.isMutateAllowed(configPropertyKey) ? (
            <>
              <Divider my="6" />

              <Flex alignItems="center">
                <Text mr="3">Report Type:</Text>
                <Box w="40">
                  <Select<CreditAgreement.Config.Reporting.ReportTemplateFileType>
                    value={_config_.type}
                    options={formatSelectOptions(
                      CreditAgreement.Config.Reporting
                        .ReportTemplateFileTypeIterable,
                    )}
                    onChange={(type) => {
                      if (type === _config_.type || !type) {
                        return;
                      }

                      let newConfig: CreditAgreement.Config.Reporting.Config;

                      switch (type) {
                        case 'docx':
                          newConfig = {
                            type,
                            input: _config_.input,
                            output: createReportingDocxOutputConfigShell(),
                          };
                          break;
                        case 'xlsx':
                          newConfig = {
                            type,
                            input: _config_.input,
                            output: createReportingXlsxOutputConfigShell(),
                          };
                          break;
                        case 'digital':
                          newConfig = {
                            type,
                            input: _config_.input,
                          };
                          break;
                        default:
                          throw finleyAssertUnreachable(type);
                      }

                      wipConfig.formProps.setFieldValue(
                        configPropertyKey,
                        newConfig,
                      );
                    }}
                  />
                </Box>
              </Flex>
            </>
          ) : null}

          <Divider my="6" />

          {!!reportTypeConfigsWithFinleyStandardTemplate.length && (
            <Box mb="16px">
              {/* @dani update the alert when it's fully live */}
              <InfoAlertWithDescription
                description={`For Servicing Platform product report [${reportTypeConfigsWithFinleyStandardTemplate.join(
                  ', ',
                )}], Finley standard input configs will be used.`}
              />
            </Box>
          )}

          {!reportTypeConfigsWithFinleyStandardTemplate.length && (
            <ReportingInputConfig
              organizationId={organizationId}
              inputConfigPropertyKey={`${configPropertyKey}.input`}
              wipConfig={wipConfig}
              reportingProperty={reportingProperty}
              queryResults={queryResults}
              setQueryResults={setQueryResults}
            />
          )}

          <Divider my="8" />

          <ReportingOutputConfig
            organizationId={organizationId}
            reportName={reportName}
            creditAgreementId={creditAgreementId}
            wipConfig={wipConfig}
            config={_config_}
            reportConfigTypes={reportConfigTypes}
            configPropertyKey={configPropertyKey}
            queryResults={queryResults}
            bankAccounts={bankAccounts}
            fileInsertAthenaQueries={_config_.input.fileInsertAthenaQueries}
          />
        </Box>
      </Td>

      <ReportingGenerateWorkleaseModal
        title="Send Worklease to Produce Report"
        isOpen={showProduceGenerateModal}
        onCancel={() => {
          setShowProduceGenerateModal(false);
        }}
        onExecute={(params: {
          worklease: Reporting.ReportingGenerateWorklease;
          parameters: Reporting.ReportingGenerateWorkleaseStatusParameterMetadata | null;
        }) => {
          produceGenerateMessage({ variables: params });
        }}
        reportConfigTypes={reportConfigTypes}
        creditAgreementId={wipConfig?.formProps.values.id ?? null}
        creditFacilityId={wipConfig?.formProps.values.creditFacilityId ?? null}
        reportName={reportName}
        reportTemplateFileType={_config_.type}
        userId={userId}
        initiatingOrganizationId={initiatingOrganizationId}
        showParameterEditor
      />

      <ReportingGenerateWorkleaseModal
        title="Generate Dev Report"
        isOpen={showDevGenerateReportModal}
        onCancel={() => {
          setShowDevGenerateReportModal(false);
        }}
        onExecute={({
          parameters,
          worklease,
        }: {
          parameters: Reporting.ReportingGenerateWorkleaseStatusParameterMetadata | null;
          worklease: Reporting.ReportingGenerateWorklease;
        }) => {
          generateDevReport({ variables: { parameters, worklease } });
        }}
        creditAgreementId={wipConfig?.formProps.values.id ?? null}
        creditFacilityId={wipConfig?.formProps.values.creditFacilityId ?? null}
        reportConfigTypes={reportConfigTypes}
        reportName={reportName}
        reportTemplateFileType={_config_.type}
        userId={userId}
        initiatingOrganizationId={initiatingOrganizationId}
        showParameterEditor
      />
    </Tr>
  );
};
